import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { scale } from '@components/frame';

export interface NetworkRequestResult {
  id: string;
  url: string;
  method: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status: 'pending' | 'success' | 'error';
  statusCode?: number;
  responseSize?: number;
  error?: string;
  networkTiming?: {
    dns?: number;
    connection?: number;
    ttfb?: number;
    download?: number;
  };
  workaroundUsed?: string;
}

export interface ReduxOperation {
  id: string;
  action: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  itemCount?: number;
  dataSize?: number;
  status: 'pending' | 'completed' | 'error';
  stateUpdateCount?: number;
  error?: string;
}

interface StressTestResultsProps {
  type: 'network' | 'redux';
  data: NetworkRequestResult[] | ReduxOperation[];
  isRunning: boolean;
  colors: any;
  isPhone: boolean;
}

const StressTestResults: React.FC<StressTestResultsProps> = ({
  type,
  data,
  isRunning,
  colors,
  isPhone
}) => {
  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    resultItem: {
      marginBottom: scale(12),
      padding: scale(12),
      backgroundColor: colors.background,
      borderRadius: scale(8),
      borderWidth: 1,
      borderColor: colors.border,
    },
    pendingItem: {
      borderLeftWidth: scale(4),
      borderLeftColor: colors.warning,
    },
    successItem: {
      borderLeftWidth: scale(4),
      borderLeftColor: colors.success,
    },
    errorItem: {
      borderLeftWidth: scale(4),
      borderLeftColor: colors.error,
    },
    itemHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: scale(8),
    },
    itemId: {
      fontSize: scale(12),
      fontWeight: '700',
      color: colors.textSecondary,
    },
    itemStatus: {
      fontSize: scale(11),
      fontWeight: '600',
      paddingHorizontal: scale(8),
      paddingVertical: scale(3),
      borderRadius: scale(4),
    },
    pendingStatus: {
      color: '#856404',
      backgroundColor: '#fff3cd',
    },
    successStatus: {
      color: '#155724',
      backgroundColor: '#d4edda',
    },
    errorStatus: {
      color: '#721c24',
      backgroundColor: '#f8d7da',
    },
    itemRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginVertical: scale(4),
    },
    itemLabel: {
      fontSize: scale(12),
      color: colors.textSecondary,
      flex: 1,
    },
    itemValue: {
      fontSize: scale(12),
      color: colors.text,
      fontWeight: '600',
      flex: 1,
      textAlign: 'right',
    },
    itemUrl: {
      fontSize: scale(11),
      color: colors.primary,
      marginBottom: scale(8),
    },
    timingGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: scale(8),
      paddingTop: scale(8),
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    timingItem: {
      width: '50%',
      paddingVertical: scale(4),
    },
    timingLabel: {
      fontSize: scale(10),
      color: colors.textSecondary,
    },
    timingValue: {
      fontSize: scale(11),
      color: colors.text,
      fontWeight: '600',
    },
    emptyState: {
      padding: scale(32),
      alignItems: 'center',
    },
    emptyText: {
      fontSize: scale(14),
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: scale(20),
    },
    workaroundBadge: {
      fontSize: scale(10),
      color: colors.primary,
      backgroundColor: colors.background,
      paddingHorizontal: scale(6),
      paddingVertical: scale(2),
      borderRadius: scale(4),
      marginTop: scale(4),
      borderWidth: 1,
      borderColor: colors.primary,
      alignSelf: 'flex-start',
    },
    errorText: {
      fontSize: scale(11),
      color: colors.error,
      marginTop: scale(4),
    },
  });

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return styles.pendingItem;
      case 'success':
      case 'completed':
        return styles.successItem;
      case 'error':
        return styles.errorItem;
      default:
        return {};
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return styles.pendingStatus;
      case 'success':
      case 'completed':
        return styles.successStatus;
      case 'error':
        return styles.errorStatus;
      default:
        return {};
    }
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '-';
    return `${duration.toFixed(0)}ms`;
  };

  const formatSize = (size?: number) => {
    if (!size) return '-';
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
    return `${(size / 1024 / 1024).toFixed(2)}MB`;
  };

  const renderNetworkRequest = (request: NetworkRequestResult) => (
    <View key={request.id} style={[styles.resultItem, getStatusStyle(request.status)]}>
      <View style={styles.itemHeader}>
        <Text style={styles.itemId}>Request #{request.id}</Text>
        <Text style={[styles.itemStatus, getStatusBadgeStyle(request.status)]}>
          {request.status.toUpperCase()}
        </Text>
      </View>

      {isPhone && (
        <Text style={styles.itemUrl} numberOfLines={2} ellipsizeMode="middle">
          {request.url}
        </Text>
      )}

      <View style={styles.itemRow}>
        <Text style={styles.itemLabel}>Duration</Text>
        <Text style={styles.itemValue}>{formatDuration(request.duration)}</Text>
      </View>

      {request.statusCode && (
        <View style={styles.itemRow}>
          <Text style={styles.itemLabel}>Status Code</Text>
          <Text style={styles.itemValue}>{request.statusCode}</Text>
        </View>
      )}

      {request.responseSize && (
        <View style={styles.itemRow}>
          <Text style={styles.itemLabel}>Response Size</Text>
          <Text style={styles.itemValue}>{formatSize(request.responseSize)}</Text>
        </View>
      )}

      {request.workaroundUsed && (
        <Text style={styles.workaroundBadge}>{request.workaroundUsed}</Text>
      )}

      {request.networkTiming && (
        <View style={styles.timingGrid}>
          <View style={styles.timingItem}>
            <Text style={styles.timingLabel}>DNS</Text>
            <Text style={styles.timingValue}>{formatDuration(request.networkTiming.dns)}</Text>
          </View>
          <View style={styles.timingItem}>
            <Text style={styles.timingLabel}>Connect</Text>
            <Text style={styles.timingValue}>{formatDuration(request.networkTiming.connection)}</Text>
          </View>
          <View style={styles.timingItem}>
            <Text style={styles.timingLabel}>TTFB</Text>
            <Text style={styles.timingValue}>{formatDuration(request.networkTiming.ttfb)}</Text>
          </View>
          <View style={styles.timingItem}>
            <Text style={styles.timingLabel}>Download</Text>
            <Text style={styles.timingValue}>{formatDuration(request.networkTiming.download)}</Text>
          </View>
        </View>
      )}

      {request.error && <Text style={styles.errorText}>Error: {request.error}</Text>}
    </View>
  );

  const renderReduxOperation = (operation: ReduxOperation) => (
    <View key={operation.id} style={[styles.resultItem, getStatusStyle(operation.status)]}>
      <View style={styles.itemHeader}>
        <Text style={styles.itemId}>Operation #{operation.id}</Text>
        <Text style={[styles.itemStatus, getStatusBadgeStyle(operation.status)]}>
          {operation.status.toUpperCase()}
        </Text>
      </View>

      <View style={styles.itemRow}>
        <Text style={styles.itemLabel}>Action</Text>
        <Text style={styles.itemValue}>{operation.action}</Text>
      </View>

      <View style={styles.itemRow}>
        <Text style={styles.itemLabel}>Duration</Text>
        <Text style={styles.itemValue}>{formatDuration(operation.duration)}</Text>
      </View>

      {operation.itemCount !== undefined && (
        <View style={styles.itemRow}>
          <Text style={styles.itemLabel}>Items Saved</Text>
          <Text style={styles.itemValue}>{operation.itemCount}</Text>
        </View>
      )}

      {operation.dataSize !== undefined && (
        <View style={styles.itemRow}>
          <Text style={styles.itemLabel}>Data Size</Text>
          <Text style={styles.itemValue}>{formatSize(operation.dataSize)}</Text>
        </View>
      )}

      {operation.stateUpdateCount !== undefined && (
        <View style={styles.itemRow}>
          <Text style={styles.itemLabel}>State Updates</Text>
          <Text style={styles.itemValue}>{operation.stateUpdateCount}</Text>
        </View>
      )}

      {operation.error && <Text style={styles.errorText}>Error: {operation.error}</Text>}
    </View>
  );

  if (!data || data.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyText}>
          {isRunning
            ? 'Test is running...\nResults will appear here.'
            : type === 'network'
            ? 'No network requests yet.\nStart the test to see results.'
            : 'No Redux operations yet.\nStart the test to see results.'}
        </Text>
      </View>
    );
  }

  // Use FlashList for better performance with large lists on mobile
  if (isPhone && data.length > 10) {
    return (
      <FlashList
        data={data}
        keyExtractor={(item: any) => item.id}
        renderItem={({ item }) =>
          type === 'network'
            ? renderNetworkRequest(item as NetworkRequestResult)
            : renderReduxOperation(item as ReduxOperation)
        }
        showsVerticalScrollIndicator={false}
      />
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {type === 'network'
        ? (data as NetworkRequestResult[]).map(renderNetworkRequest)
        : (data as ReduxOperation[]).map(renderReduxOperation)}
    </ScrollView>
  );
};

export default StressTestResults;